/**
 * "Who we've asked on this case" — the top of a case's right rail (proposal
 * 2, kanban 31f2809d). Each ask that went out: who, what, when, how it
 * reached them on each medium, reply state, the next reminder and how
 * overdue it is, with Nudge (or Chase, once overdue) to send a reminder now.
 * Checklist items on a colleague that nobody was asked about say "Never
 * asked" with an Ask button. Answers sit underneath; an unread one opens in
 * the answer drawer (proposal 1), which never marks it seen by itself.
 */
import { handoffWords } from './askKind';
import { useState } from 'react';

import { trackEvent } from '../../services/portalTracking';
import {
  discardDataRequest,
  dismissDataRequest,
  nudgeDataRequest,
  type DoorDataRequestRead,
} from '../../services/qcpDataRequests';
import type { DoorCaseTaskRead } from '../../services/qcpInvestigations';
import {
  askRail,
  deliveryLine,
  overdueCount,
  rowNote,
  sentWords,
  type AskRailRow,
} from '../../pages/quality-triage/askRailView';
import { answeredWhen, askedOn } from '../../pages/quality-triage/answersView';
import type { HandOffRow } from '../../pages/quality-triage/handOffView';
import { taskAskDesk, taskAskStep } from '../portal/caseTaskAsks';
import { revealDraft } from './asksView';
import { DismissControl } from './DismissControl';
import styles from './AskedOnCasePanel.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

export interface AskedOnCasePanelProps {
  /** Every ask on the case or item (asksForCase / asksForThread). */
  asks: DoorDataRequestRead[];
  /** The case's checklist (empty for an item with no case). */
  tasks: DoorCaseTaskRead[];
  /** What the panel sits on, recorded with every click. */
  scope: { investigation_no?: string | null; thread_no?: string | null };
  /** Fold a changed ask (nudged) into the door's list. */
  onUpdated: (request: DoorDataRequestRead) => void;
  /** Open the Ask modal for a never-asked item. Absent: no Ask buttons. */
  onAsk?: (task: DoorCaseTaskRead) => void;
  /** Open one answer in the drawer. */
  onRead: (requestId: string) => void;
  /** The item is with someone else: the hand-off email and its reply are
   *  the first "ask" (UX round 2026-10-13). */
  handOff?: HandOffRow | null;
  /** Where Merlin's drafts sit with their Send button (an element id). */
  draftsTargetId?: string;
  now?: Date;
}

/** The daily sweep found this answer elsewhere (kanban 3eed0be5): the person
 *  asked never replied, so the row must not read as theirs. */
function foundByMerlin(r: { responses?: { channel: string; created_at: string }[] }): boolean {
  const last = [...(r.responses ?? [])].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))[0];
  return last?.channel === 'found';
}

function errorText(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'That did not go through. Try again in a moment.';
}

function ReplyPill({ row, now }: { row: AskRailRow; now: Date }) {
  if (row.reply === 'unread') return <span className={`${styles.pill} ${styles.pillNew}`}>New answer</span>;
  if (row.reply === 'answered') return <span className={`${styles.pill} ${styles.pillOk}`}>Answered</span>;
  if (row.reply === 'handed') {
    return (
      <span className={`${styles.pill} ${styles.pillOk}`} data-testid="asked-handed">
        Passed along · {handoffWords(row.request.handoff, row.person.split(' ')[0] || row.person)}
      </span>
    );
  }
  if (row.overdueWords) return <span className={`${styles.pill} ${styles.pillBad}`}>⏰ {row.overdueWords}</span>;
  return <span className={`${styles.pill} ${styles.pillOk}`}>{sentWords(row, now)}</span>;
}

export function AskedOnCasePanel({
  asks,
  tasks,
  scope,
  onUpdated,
  onAsk,
  onRead,
  handOff = null,
  draftsTargetId = 'case-asks',
  now = new Date(),
}: AskedOnCasePanelProps) {
  // A read-only viewer sees who was asked and when; it sends nothing (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  const rail = askRail(asks, tasks, now);
  const overdue = overdueCount(rail.rows);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [nudged, setNudged] = useState<Record<string, boolean>>({});

  const remind = async (row: AskRailRow) => {
    const id = row.request.id;
    const action = row.overdueWords ? 'asks_panel_chase' : 'asks_panel_nudge';
    setBusyId(id);
    setErrors((e) => ({ ...e, [id]: '' }));
    try {
      const updated = await nudgeDataRequest(id);
      trackEvent('action', action, { ...scope, data_request_id: id, result: 'request_sent' });
      setNudged((n) => ({ ...n, [id]: true }));
      onUpdated(updated);
    } catch (e) {
      trackEvent('action', action, { ...scope, data_request_id: id, result: 'failed' });
      setErrors((cur) => ({ ...cur, [id]: errorText(e) }));
    } finally {
      setBusyId(null);
    }
  };

  // Dismiss from the rail (kanban 59a1ee04): a draft is thrown away, a
  // waiting ask withdrawn; the control records the press and shows a refusal.
  const dismissDraft = async (draft: DoorDataRequestRead, note: string | null) => {
    onUpdated(await discardDataRequest(draft.id, 'discarded by Jess', note));
  };
  const dismissAsk = async (request: DoorDataRequestRead, note: string | null) => {
    onUpdated(await dismissDataRequest(request.id, undefined, note));
  };
  const dismissTrack = (id: string, state: 'draft' | 'waiting') => ({
    plain: 'qcp_ask_dismiss',
    note: 'qcp_ask_dismiss_note',
    // The note form is named by what it reasons about, never by the person
    // in its accessible name (kanban 529c0851).
    form: state === 'draft' ? 'qcp_draft_reason' : 'qcp_ask_reason',
    targets: { data_request_id: id, thread_id: scope.thread_no, case_id: scope.investigation_no },
    context: { ...scope, data_request_id: id, surface: 'asks_panel', state },
  });

  const empty =
    rail.rows.length === 0 && rail.neverAsked.length === 0 && rail.drafts.length === 0 && !handOff;
  const noun = scope.investigation_no ? 'case' : 'item';
  const title = `Who we\u2019ve asked on this ${noun}`;

  return (
    <section className={styles.panel} aria-label={title} data-testid="asked-on-case">
      <h3 className={styles.head}>
        {title}
        {overdue > 0 && (
          <span className={`${styles.pill} ${styles.pillWarn}`} data-testid="asked-overdue-count">
            {overdue} overdue
          </span>
        )}
      </h3>
      {empty && <p className={styles.muted}>Nobody has been asked anything on this {noun} yet.</p>}
      <ul className={styles.list}>
        {handOff && (
          <li className={styles.row} data-testid="asked-handoff" data-reply={handOff.reply ? 'answered' : 'waiting'}>
            <div className={styles.text}>
              <b>{handOff.to}</b>
              <div className={styles.question}>
                {handOff.gate === 'held'
                  ? `Has this ${noun}: not emailed, Merlin judged they already know it`
                  : handOff.gate === 'pending'
                    ? `Has this ${noun}: the hand-off email waits for Merlin's check (do they already know it?)`
                    : `Has this ${noun}: sent to them by the hand-off email${handOff.sentAt ? ` on ${askedOn(handOff.sentAt)}` : ''}`}
              </div>
              <div className={styles.status}>
                {handOff.reply ? (
                  <span className={`${styles.pill} ${styles.pillOk}`}>
                    Replied {answeredWhen(handOff.reply.at, now)}
                  </span>
                ) : handOff.gate ? (
                  <span className={styles.pill} data-testid="asked-handoff-gate">
                    {handOff.gate === 'held' ? 'Held, not emailed' : 'Checking before sending'}
                  </span>
                ) : (
                  <span className={styles.pill}>No reply yet</span>
                )}
              </div>
              {handOff.reply?.excerpt && (
                <div className={styles.muted} data-testid="asked-handoff-excerpt">
                  &ldquo;{handOff.reply.excerpt}&rdquo;
                </div>
              )}
            </div>
          </li>
        )}
        {rail.drafts.map((draft) => (
          <li key={draft.id} className={styles.row} data-testid="asked-draft">
            <div className={styles.text}>
              <b>{draft.target_name}</b>
              <div className={styles.question}>{draft.question_title}</div>
              <div className={styles.status}>
                <span className={`${styles.pill} ${styles.pillWarn}`}>Draft: not sent</span>
                <span className={styles.muted}>Merlin wrote it; nothing goes out until you press Send</span>
              </div>
            </div>
            {!readOnly && (<div className={styles.rowActions}>
              <button
                type="button"
                className={styles.primary}
                onClick={() => {
                  const result = revealDraft(draft, draftsTargetId);
                  trackEvent('action', 'asks_panel_review_draft', { ...scope, data_request_id: draft.id, result });
                }}
              >
                Review and send
              </button>
              <DismissControl
                what={`the draft to ${draft.target_name}`}
                label="Don't ask"
                noteLabel="say why"
                placeholder="Why not? e.g. already answered in the orders review meeting"
                size="row"
                onDismiss={(note) => dismissDraft(draft, note)}
                track={dismissTrack(draft.id, 'draft')}
              />
            </div>)}
          </li>
        ))}
        {rail.rows.map((row) => {
          const id = row.request.id;
          const line = deliveryLine(row.deliveries);
          const canRemind = row.reply === 'waiting';
          return (
            <li key={id} className={styles.row} data-testid="asked-row" data-reply={row.reply}>
              <div className={styles.text}>
                <b>{row.person}</b>
                <div className={styles.question}>{row.question}</div>
                <div className={styles.status}>
                  <ReplyPill row={row} now={now} />
                  {line && row.reply === 'waiting' && (
                    <span className={styles.muted} data-testid="asked-delivery">
                      {line}
                    </span>
                  )}
                </div>
                <div className={styles.muted}>{rowNote(row)}</div>
                {nudged[id] && <div className={styles.done}>Reminder on its way.</div>}
                {errors[id] && (
                  <div className={styles.error} role="alert">
                    {errors[id]}
                  </div>
                )}
              </div>
              {canRemind && !readOnly && (
                <div className={styles.rowActions}>
                  <button
                    type="button"
                    className={row.overdueWords ? styles.primary : styles.secondary}
                    disabled={busyId === id || nudged[id]}
                    onClick={() => void remind(row)}
                    title={`Send ${row.person} a reminder now`}
                    data-action="qcp_ask_nudge_send"
                    data-target-data-request-id={id}
                  >
                    {row.overdueWords ? 'Chase' : 'Nudge'}
                  </button>
                  <DismissControl
                    what={`the ask to ${row.person}`}
                    label="Dismiss"
                    noteLabel="say why"
                    size="row"
                    danger
                    onDismiss={(note) => dismissAsk(row.request, note)}
                    track={dismissTrack(id, 'waiting')}
                  />
                </div>
              )}
              {row.reply === 'unread' && (
                <button
                  type="button"
                  className={styles.primary}
                  onClick={() => {
                    trackEvent('action', 'asks_panel_open_answer', { ...scope, data_request_id: id, result: 'panel_opened' });
                    onRead(id);
                  }}
                >
                  Read
                </button>
              )}
            </li>
          );
        })}
        {rail.neverAsked.map((task) => {
          // Colleague rows only: a customer's item is never asked from here
          // (its row carries a recommendation instead, kanban d3915b86).
          const desk = taskAskDesk(task);
          const who = desk.desk || 'A colleague';
          return (
            <li key={task.id} className={styles.row} data-testid="never-asked-row" data-owner={task.owner}>
              <div className={styles.text}>
                <b>{who}</b>
                <div className={styles.question}>{taskAskStep(task)}</div>
                <div className={styles.status}>
                  <span className={`${styles.pill} ${styles.pillWarn}`}>Never asked</span>
                  <span className={styles.muted}>
                    listed as &ldquo;on a colleague&rdquo; but nothing was sent
                  </span>
                </div>
              </div>
              {onAsk && !readOnly && (
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={() => {
                    trackEvent('action', 'asks_panel_ask_never_asked', { ...scope, task_id: task.id, result: 'modal_opened' });
                    onAsk(task);
                  }}
                >
                  {desk.label}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <h3 className={`${styles.head} ${styles.answersHead}`}>
        Answers
        {rail.answers.length === 0 && <span className={styles.headNote}>none yet</span>}
      </h3>
      {rail.answers.length === 0 ? (
        <p className={styles.muted}>When someone answers, it shows here and in Triage, tied to this {noun}.</p>
      ) : (
        <ul className={styles.list}>
          {rail.answers.map((r) => (
            <li key={r.id} className={styles.answer} data-testid="asked-answer" data-unread={r.answer_unseen ? 'true' : 'false'}>
              <button
                type="button"
                className={styles.answerBtn}
                onClick={() => {
                  trackEvent('action', 'asks_panel_open_answer', { ...scope, data_request_id: r.id, result: 'panel_opened' });
                  onRead(r.id);
                }}
              >
                {r.answer_unseen && <span className={styles.dot} aria-label="unread" />}
                {foundByMerlin(r) ? (
                  <>
                    <b>Merlin</b> found the answer to &ldquo;{r.question_title}&rdquo;
                  </>
                ) : (
                  <>
                    <b>{r.target_name}</b> answered &ldquo;{r.question_title}&rdquo;
                  </>
                )}
                {r.answered_at && <span className={styles.muted}> · {answeredWhen(r.answered_at, now)}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default AskedOnCasePanel;
